import React, { FC } from 'react'
import styled from 'styled-components'
import MorphingArrowIcon from './MorphingArrowIcon'
import MorphingRotateIcon from './MorphingRotateIcon'
import MorphingHamburgerIcon from './MorphingHamburgerIcon'

const StyledButton = styled.button`
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;
  will-change: border-radius;
  transform: translateZ(0);

  /* Synchronized transition matching border-radius, color and background */
  transition-property: border-radius, background-color, border-color, color, box-shadow;
  transition-duration: 700ms;
  transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  &.rounded-large {
    border-radius: min(var(--radius-large, 9999px), calc(var(--spacing-extra-huge, 56px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-large, 24px), calc(var(--spacing-extra-huge, 56px) / 2));
      }
    }
  }

  &.rounded-base {
    border-radius: min(var(--radius-base, 9999px), calc(var(--spacing-huge, 48px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-base, 16px), calc(var(--spacing-huge, 48px) / 2));
      }
    }
  }

  &.rounded-small {
    border-radius: min(var(--radius-small, 9999px), calc(var(--wcag-min-target, 44px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-small, 12px), calc(var(--wcag-min-target, 44px) / 2));
      }
    }
  }

  .button-slide-row {
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform: translate3d(0, var(--tw-translate-y, 0), 0);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`

export enum ButtonSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export interface IButtonIconProps {
  icon?: React.ReactNode
  children?: React.ReactNode
  hoverIcon?: React.ReactNode
  morphingIcon?: 'arrow-to-x' | 'rotate-ccw' | 'rotate-cw' | 'hamburger' | React.ReactNode
  isOpen?: boolean
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  size?: ButtonSizes
  isOutline?: boolean
  isDisabled?: boolean
  isCircle?: boolean
  type?: 'button' | 'submit' | 'reset'
  noSlide?: boolean
  title?: string
  form?: string
}

const RADIUS_CLASSES: Record<ButtonSizes, string> = {
  [ButtonSizes.small]: 'rounded-small hover:rounded-hover-small lg:hover:rounded-hover-small',
  [ButtonSizes.medium]: 'rounded-base hover:rounded-hover-base lg:hover:rounded-hover-base',
  [ButtonSizes.large]: 'rounded-large hover:rounded-hover-large lg:hover:rounded-hover-large',
}

const SQUARE_SIZES: Record<ButtonSizes, string> = {
  [ButtonSizes.small]: 'w-wcag-min-target h-wcag-min-target min-w-wcag-min-target min-h-wcag-min-target',
  [ButtonSizes.medium]: 'w-huge h-huge min-w-huge min-h-huge',
  [ButtonSizes.large]: 'w-extra-huge h-extra-huge min-w-extra-huge min-h-extra-huge',
}

const OUTLINE_STYLES =
  'bg-transparent border-1 border-outline-low text-on-surface lg:hover:bg-transparent'

const FILLED_STYLES = 'bg-primary text-on-primary-unstable hover:bg-primary-hover [&_svg]:text-on-primary-unstable [&_svg]:stroke-on-primary-unstable'

const DISABLED_OUTLINE_STYLES =
  'bg-transparent border-2 border-outline-low text-on-surface-disabled cursor-not-allowed pointer-events-none'

const DISABLED_FILLED_STYLES =
  'bg-surface-disabled text-on-surface-disabled cursor-not-allowed pointer-events-none'

export const ButtonIcon: FC<IButtonIconProps> = ({
  onClick,
  className = '',
  icon,
  children,
  hoverIcon,
  morphingIcon,
  isOpen = false,
  size = ButtonSizes.large,
  isOutline = false,
  isDisabled = false,
  isCircle = false,
  type = 'button',
  noSlide = false,
  title,
  form,
}) => {
  const [isHovered, setIsHovered] = React.useState(false)
  const isMorphing = Boolean(morphingIcon)
  const iconColor = !isOutline ? '#FFFFFF' : 'currentColor'

  const resolvedContent = (() => {
    if (morphingIcon === 'arrow-to-x') {
      return <MorphingArrowIcon color={iconColor} isHovered={isHovered} />
    }
    if (morphingIcon === 'rotate-ccw') {
      return <MorphingRotateIcon direction="ccw" color={iconColor} isHovered={isHovered} />
    }
    if (morphingIcon === 'rotate-cw') {
      return <MorphingRotateIcon direction="cw" color={iconColor} isHovered={isHovered} />
    }
    if (morphingIcon === 'hamburger') {
      return <MorphingHamburgerIcon color={iconColor} isHovered={isHovered} isOpen={isOpen} />
    }
    if (React.isValidElement(morphingIcon)) {
      return morphingIcon
    }
    return icon || children
  })()

  const sizeStyles = isCircle
    ? 'w-extra-huge h-extra-huge min-w-extra-huge min-h-extra-huge shrink-0 p-0'
    : `${SQUARE_SIZES[size]} shrink-0 p-0`

  const radiusStyles = isCircle ? 'rounded-full' : RADIUS_CLASSES[size]

  const typeStyles = isDisabled
    ? (isOutline ? DISABLED_OUTLINE_STYLES : DISABLED_FILLED_STYLES)
    : (isOutline ? OUTLINE_STYLES : FILLED_STYLES)

  const buttonStyles = `group relative overflow-hidden inline-flex items-center justify-center select-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${radiusStyles} ${sizeStyles} ${typeStyles} ${className}`.trim()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) return
    onClick?.(e)
  }

  const renderContent = (node: React.ReactNode) => (
    <span className="inline-flex items-center justify-center pointer-events-none text-current">
      {node}
    </span>
  )

  return (
    <StyledButton
      type={type}
      form={form}
      title={title}
      className={buttonStyles}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isDisabled}
    >
      {noSlide || isMorphing ? (
        renderContent(resolvedContent)
      ) : (
        <span className="relative inline-grid items-center justify-center h-full w-full">
          {/* Visible normal row */}
          <span className="button-slide-row row-start-1 col-start-1 h-full w-full inline-flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] lg:group-hover:-translate-y-full">
            {renderContent(resolvedContent)}
          </span>
          {/* Visible hover row */}
          <span className="button-slide-row row-start-1 col-start-1 h-full w-full inline-flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] translate-y-full lg:group-hover:translate-y-0">
            {renderContent(hoverIcon || resolvedContent)}
          </span>
          {/* Sizing placeholder */}
          <span
            className="row-start-1 col-start-1 h-full w-full invisible opacity-0 pointer-events-none inline-flex items-center justify-center"
            aria-hidden="true"
          >
            {renderContent(resolvedContent)}
          </span>
        </span>
      )}
    </StyledButton>
  )
}

export default ButtonIcon
